import { useEffect, useRef, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import m61 from './Asset/m6-1.webp';
import m62 from './Asset/m6-2.webp';
import m63 from './Asset/m6-3.webp';
import m64 from './Asset/m6-4.webp';
import m65 from './Asset/m6-5.webp';
import m66 from './Asset/m6-6.webp';
import PageSeo, {
  absoluteUrl,
  breadcrumbJsonLd,
  keywordSets,
  productJsonLd,
} from './PageSeo';

const m6Variants = [
  {
    name: 'Standard 7-Seater',
    price: 'Rp383.000.000',
    priceValue: 383000000,
    summary: 'Pilihan paling efisien untuk keluarga yang ingin mulai beralih ke MPV listrik.',
    details: [
      { label: 'Konfigurasi Kursi', value: '7-Seater' },
      { label: 'Jarak Mengemudi NEDC', value: '420 km' },
      { label: 'Torsi Maksimum', value: '310 Nm' },
      { label: 'Kapasitas Baterai', value: '55,4 kWh' },
    ],
  },
  {
    name: 'Superior 7-Seater',
    price: 'Rp423.000.000',
    priceValue: 423000000,
    summary: 'Jarak tempuh lebih jauh dan fitur lebih lengkap untuk perjalanan keluarga.',
    details: [
      { label: 'Konfigurasi Kursi', value: '7-Seater' },
      { label: 'Jarak Mengemudi NEDC', value: '530 km' },
      { label: 'Torsi Maksimum', value: '310 Nm' },
      { label: 'Kapasitas Baterai', value: '71,8 kWh' },
    ],
  },
  {
    name: 'Superior Captain Seat',
    price: 'Rp433.000.000',
    priceValue: 433000000,
    summary: 'Kabin 6-seater dengan captain seat untuk kenyamanan keluarga dan eksekutif.',
    details: [
      { label: 'Konfigurasi Kursi', value: '6-Seater' },
      { label: 'Jarak Mengemudi NEDC', value: '530 km' },
      { label: 'Torsi Maksimum', value: '310 Nm' },
      { label: 'Kapasitas Baterai', value: '71,8 kWh' },
    ],
  },
];

const m6Highlights = [
  {
    title: 'MPV Listrik 6-7 Seater',
    text: 'BYD M6 cocok untuk keluarga, bisnis, antar jemput, dan aktivitas harian dengan kabin lega.',
  },
  {
    title: 'Jarak Tempuh Hingga 530 km',
    text: 'Varian Superior memberi fleksibilitas lebih untuk rute BSD-Jakarta, luar kota, dan perjalanan akhir pekan.',
  },
  {
    title: 'V2L untuk Keluarga',
    text: 'Gunakan daya mobil untuk perangkat outdoor, camping, laptop, alat makan, atau kebutuhan darurat.',
  },
  {
    title: 'Blade Battery',
    text: 'Teknologi baterai BYD memberi rasa aman, efisien, dan nyaman untuk penggunaan jangka panjang.',
  },
];

const serviceAreas = [
  'BSD Serpong',
  'Gading Serpong',
  'Alam Sutera',
  'Bintaro',
  'Tangerang Selatan',
  'Jakarta',
];

const m6Faqs = [
  {
    question: 'Berapa harga BYD M6 di BYD BSD Serpong?',
    answer:
      'Harga BYD M6 mulai dari Rp383.000.000 untuk Standard 7-Seater, Rp423.000.000 untuk Superior 7-Seater, dan Rp433.000.000 untuk Superior Captain Seat. Hubungi Junius untuk cek promo dan simulasi cicilan terbaru.',
  },
  {
    question: 'Apa beda BYD M6 Standard, Superior, dan Captain Seat?',
    answer:
      'Standard 7-Seater adalah varian paling efisien. Superior 7-Seater punya baterai lebih besar dan jarak tempuh hingga 530 km NEDC. Superior Captain Seat memakai konfigurasi 6-seater yang lebih nyaman di baris kedua.',
  },
  {
    question: 'Apakah BYD M6 cocok untuk keluarga?',
    answer:
      'Ya. BYD M6 dirancang sebagai MPV listrik keluarga dengan pilihan 6-7 seater, kabin lega, bagasi praktis, V2L, dan biaya operasional yang lebih hemat dibanding mobil bensin.',
  },
  {
    question: 'Apakah BYD M6 bisa test drive di BSD Serpong?',
    answer:
      'Bisa. Anda bisa menghubungi Junius untuk mengatur jadwal test drive BYD M6 di BYD BSD Serpong sesuai ketersediaan unit.',
  },
  {
    question: 'Apakah BYD M6 tersedia promo atau cicilan?',
    answer:
      'Promo dan paket cicilan dapat berubah mengikuti periode program dan stok unit. Chat Junius untuk mendapatkan info terbaru, estimasi DP, tenor, dan angsuran.',
  },
];

function buildWhatsAppUrl(message) {
  return `https://api.whatsapp.com/send?phone=6281286864825&text=${encodeURIComponent(message)}`;
}

function JelajahiM6() {
  const sections = useRef([]);
  const [visibleSections, setVisibleSections] = useState({});

  const images = [
    {
      src: m61,
      title: 'BYD M6 BSD Serpong',
      subtitle: 'Harga, promo, simulasi cicilan, dan test drive MPV listrik keluarga bersama Junius.',
      isHero: true,
      specs: [
        { value: '530 km', label: 'NEDC Superior' },
        { value: '6-7 Seat', label: 'Pilihan kabin' },
        { value: 'Rp383 Juta', label: 'Harga mulai' },
      ],
    },
    {
      src: m62,
      title: 'Desain MPV Listrik Modern',
      description:
        'BYD M6 menggabungkan desain aerodinamis, kabin luas, dan karakter praktis untuk keluarga di BSD Serpong, Tangerang Selatan, dan Jabodetabek.',
    },
    {
      src: m63,
      title: 'V2L untuk Aktivitas Keluarga',
      description:
        'Fitur Vehicle-to-Load membantu Anda menggunakan daya mobil untuk perangkat outdoor, acara keluarga, alat kerja, atau kebutuhan darurat.',
    },
    {
      src: m64,
      title: 'Kokpit Cerdas dan Nyaman',
      description:
        'Interior BYD M6 dirancang untuk perjalanan harian maupun jarak jauh, dengan tampilan modern, kontrol praktis, dan suasana kabin yang nyaman.',
    },
    {
      src: m65,
      title: 'Blade Battery yang Aman',
      description:
        'Teknologi Blade Battery BYD dikenal aman dan efisien, memberi rasa tenang untuk penggunaan keluarga, perjalanan harian, dan kebutuhan mobilitas jangka panjang.',
    },
    {
      src: m66,
      title: 'BYD M6 untuk Keluarga Modern',
      description:
        'Pilih BYD M6 jika Anda mencari MPV listrik 6-7 seater dengan biaya operasional hemat, kabin lega, dan fitur yang mendukung aktivitas keluarga.',
    },
  ];

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: m6Faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  const vehicleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Vehicle',
    name: 'BYD M6',
    brand: {
      '@type': 'Brand',
      name: 'BYD',
    },
    image: absoluteUrl(m61),
    description:
      'BYD M6 adalah MPV listrik 6-7 seater untuk keluarga dengan jarak tempuh hingga 530 km NEDC, fitur V2L, dan Blade Battery.',
    offers: {
      '@type': 'AggregateOffer',
      url: absoluteUrl('/jelajahi-m6'),
      priceCurrency: 'IDR',
      lowPrice: m6Variants[0].priceValue,
      highPrice: m6Variants[2].priceValue,
      offerCount: m6Variants.length,
      availability: 'https://schema.org/InStock',
      seller: {
        '@id': 'https://bydbsdserpong.com/#dealer',
      },
    },
  };

  useEffect(() => {
    const currentSections = sections.current;

    const observer = new IntersectionObserver(
      (entries) => {
        setVisibleSections((prev) => {
          const updated = { ...prev };
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              updated[entry.target.dataset.index] = true;
            }
          });
          return updated;
        });
      },
      { threshold: 0.35 },
    );

    currentSections.forEach((section) => {
      if (section) observer.observe(section);
    });

    return () => {
      currentSections.forEach((section) => {
        if (section) observer.unobserve(section);
      });
    };
  }, []);

  return (
    <Box component='main'>
      <PageSeo
        title='BYD M6 BSD Serpong | Harga Rp383 Juta, Promo & Test Drive'
        description='Cek harga BYD M6 Standard, Superior, dan Captain Seat di BYD BSD Serpong. MPV listrik 6-7 seater, jarak tempuh hingga 530 km, V2L, promo, cicilan, dan test drive via Junius.'
        keywords={[
          ...keywordSets.localDealer,
          'harga BYD M6',
          'harga BYD M6 BSD Serpong',
          'promo BYD M6',
          'test drive BYD M6',
          'BYD M6 Tangerang Selatan',
          'BYD M6 7 seater',
          'BYD M6 6 seater',
          'BYD M6 Captain Seat',
          'brosur BYD M6 PDF',
          'download brosur BYD M6',
          'MPV listrik keluarga BYD',
          'sales BYD M6 Junius',
        ]}
        path='/jelajahi-m6'
        image={m61}
        jsonLd={[
          productJsonLd({
            name: 'BYD M6',
            description:
              'MPV listrik BYD M6 untuk keluarga dengan pilihan Standard, Superior, dan Superior Captain Seat, jarak tempuh hingga 530 km NEDC, fitur V2L, dan Blade Battery.',
            image: m61,
            path: '/jelajahi-m6',
            lowPrice: m6Variants[0].priceValue,
            highPrice: m6Variants[2].priceValue,
            offerCount: m6Variants.length,
          }),
          vehicleJsonLd,
          faqJsonLd,
          breadcrumbJsonLd([
            { name: 'Beranda', path: '/' },
            { name: 'BYD M6', path: '/jelajahi-m6' },
          ]),
        ]}
      />

      {images.map((img, index) => (
        <Box
          key={img.title}
          component='section'
          ref={(el) => (sections.current[index] = el)}
          data-index={index}
          aria-label={img.title}
          sx={{
            position: 'relative',
            minHeight: { xs: img.isHero ? 620 : 560, md: '100vh' },
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            backgroundColor: '#111827',
          }}
        >
          <Box
            component='img'
            src={img.src}
            alt={
              img.isHero
                ? 'BYD M6 BSD Serpong - MPV listrik keluarga untuk promo dan test drive'
                : `${img.title} BYD M6`
            }
            loading={index === 0 ? 'eager' : 'lazy'}
            sx={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: img.isHero
                ? 'linear-gradient(90deg, rgba(248,250,252,0.9) 0%, rgba(248,250,252,0.62) 44%, rgba(248,250,252,0.08) 100%)'
                : 'linear-gradient(180deg, rgba(8,13,21,0.12) 0%, rgba(8,13,21,0.66) 100%)',
            }}
          />

          {img.isHero ? (
            <Box
              sx={{
                position: 'relative',
                zIndex: 1,
                width: '100%',
                maxWidth: 1180,
                px: { xs: 2, md: 6 },
                color: '#111827',
              }}
            >
              <Typography
                sx={{
                  display: 'inline-flex',
                  px: 1.4,
                  py: 0.65,
                  borderRadius: '8px',
                  backgroundColor: '#2d6cdf',
                  color: '#fff',
                  fontWeight: 900,
                  fontSize: '0.82rem',
                  textTransform: 'uppercase',
                }}
              >
                Harga BYD M6 BSD Serpong
              </Typography>
              <Typography
                variant='h1'
                component='h1'
                sx={{
                  mt: 2,
                  maxWidth: 760,
                  fontWeight: 900,
                  lineHeight: 1,
                  fontSize: { xs: '2.45rem', sm: '3.4rem', md: '5rem' },
                }}
              >
                BYD M6, MPV Listrik Keluarga 6-7 Seater
              </Typography>
              <Typography
                sx={{
                  mt: 2,
                  maxWidth: 650,
                  color: '#374151',
                  fontSize: { xs: '1rem', md: '1.15rem' },
                  lineHeight: 1.75,
                }}
              >
                {img.subtitle}
              </Typography>
              <Box
                sx={{
                  mt: 3,
                  display: 'flex',
                  gap: 1.5,
                  flexWrap: 'wrap',
                }}
              >
                <Button
                  component='a'
                  href={buildWhatsAppUrl(
                    'Halo Junius, saya mau tanya harga, promo, dan jadwal test drive BYD M6 di BYD BSD Serpong.',
                  )}
                  target='_blank'
                  rel='noopener noreferrer'
                  variant='contained'
                  sx={{
                    borderRadius: '8px',
                    px: 3,
                    py: 1.15,
                    fontWeight: 900,
                    textTransform: 'none',
                    backgroundColor: '#2d6cdf',
                    '&:hover': { backgroundColor: '#1f5fc8' },
                  }}
                >
                  Chat Harga & Promo
                </Button>
                <Button
                  component={RouterLink}
                  to='/pricing'
                  variant='outlined'
                  sx={{
                    borderRadius: '8px',
                    px: 3,
                    py: 1.15,
                    fontWeight: 900,
                    textTransform: 'none',
                    color: '#111827',
                    borderColor: '#111827',
                    '&:hover': {
                      borderColor: '#2d6cdf',
                      color: '#2d6cdf',
                      backgroundColor: '#edf4ff',
                    },
                  }}
                >
                  Lihat Price List BYD
                </Button>
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                position: 'relative',
                zIndex: 1,
                maxWidth: 900,
                mx: 'auto',
                px: { xs: 2, md: 4 },
                color: '#fff',
                textAlign: 'center',
                transform: visibleSections[index] ? 'translateY(0)' : 'translateY(60px)',
                opacity: visibleSections[index] ? 1 : 0,
                transition: 'all 0.8s ease-in-out',
              }}
            >
              <Typography
                component='h2'
                sx={{
                  fontWeight: 900,
                  lineHeight: 1.08,
                  fontSize: { xs: '1.85rem', sm: '2.4rem', md: '3.4rem' },
                  textShadow: '0 2px 18px rgba(0,0,0,0.45)',
                }}
              >
                {img.title}
              </Typography>
              {img.description && (
                <Typography
                  sx={{
                    mt: 2,
                    mx: 'auto',
                    maxWidth: 780,
                    color: 'rgba(255,255,255,0.88)',
                    lineHeight: 1.75,
                    fontSize: { xs: '0.96rem', md: '1.08rem' },
                    backgroundColor: 'rgba(8,13,21,0.46)',
                    borderRadius: '8px',
                    p: { xs: 2, md: 2.5 },
                  }}
                >
                  {img.description}
                </Typography>
              )}
              {img.src === m66 && (
                <Button
                  href={`${process.env.PUBLIC_URL}/files/Byd%20m6.pdf`}
                  download='Brosur-BYD-M6.pdf'
                  variant='outlined'
                  sx={{
                    mt: 3,
                    borderRadius: '8px',
                    borderColor: '#fff',
                    color: '#fff',
                    fontWeight: 900,
                    textTransform: 'none',
                    px: 3,
                    py: 1.1,
                    '&:hover': {
                      backgroundColor: '#fff',
                      color: '#111827',
                    },
                  }}
                >
                  Download Brosur BYD M6
                </Button>
              )}
            </Box>
          )}

          {img.specs && (
            <Box
              sx={{
                position: 'absolute',
                left: { xs: 16, md: 72 },
                right: { xs: 16, md: 72 },
                bottom: { xs: 28, md: 48 },
                zIndex: 1,
                display: 'grid',
                gridTemplateColumns: { xs: '1fr 1fr 1fr', md: 'repeat(3, 180px)' },
                gap: { xs: 1, md: 2 },
              }}
            >
              {img.specs.map((spec) => (
                <Box
                  key={spec.label}
                  sx={{
                    p: { xs: 1.2, md: 2 },
                    borderRadius: '8px',
                    backgroundColor: 'rgba(255,255,255,0.78)',
                    border: '1px solid rgba(17,24,39,0.12)',
                    color: '#111827',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: { xs: '1rem', md: '1.5rem' },
                      fontWeight: 900,
                      lineHeight: 1,
                    }}
                  >
                    {spec.value}
                  </Typography>
                  <Typography sx={{ mt: 0.6, fontSize: { xs: '0.72rem', md: '0.86rem' } }}>
                    {spec.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      ))}

      <Box
        component='section'
        id='harga-byd-m6'
        aria-labelledby='harga-byd-m6-title'
        sx={{
          px: { xs: 2, md: 6 },
          py: { xs: 5, md: 8 },
          backgroundColor: '#fff',
        }}
      >
        <Box sx={{ maxWidth: 1180, mx: 'auto' }}>
          <Typography
            id='harga-byd-m6-title'
            component='h2'
            sx={{
              fontWeight: 900,
              color: '#111827',
              lineHeight: 1.12,
              fontSize: { xs: '1.9rem', md: '2.6rem' },
            }}
          >
            Harga BYD M6 BSD Serpong
          </Typography>
          <Typography sx={{ mt: 1.5, color: '#4b5563', lineHeight: 1.75, maxWidth: 820 }}>
            BYD M6 tersedia dalam pilihan 7-seater dan 6-seater Captain Seat.
            Hubungi Junius untuk promo, ketersediaan warna, estimasi unit, dan simulasi
            cicilan terbaru.
          </Typography>

          <Box
            sx={{
              mt: 3,
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: 2.5,
            }}
          >
            {m6Variants.map((variant) => (
              <Box
                key={variant.name}
                sx={{
                  p: { xs: 2.5, md: 3 },
                  borderRadius: '8px',
                  border: '1px solid rgba(17,24,39,0.1)',
                  backgroundColor: '#f8fafc',
                  boxShadow: '0 14px 30px rgba(16,24,40,0.08)',
                }}
              >
                <Typography component='h3' sx={{ fontSize: '1.25rem', fontWeight: 900 }}>
                  {variant.name}
                </Typography>
                <Typography
                  sx={{
                    mt: 1,
                    color: '#2d6cdf',
                    fontSize: { xs: '1.85rem', md: '2.1rem' },
                    fontWeight: 900,
                    lineHeight: 1,
                  }}
                >
                  {variant.price}
                </Typography>
                <Typography sx={{ mt: 1.2, color: '#4b5563', lineHeight: 1.6 }}>
                  {variant.summary}
                </Typography>
                <Box sx={{ mt: 2.2, display: 'grid', gap: 1 }}>
                  {variant.details.map((item) => (
                    <Box
                      key={item.label}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 2,
                        py: 1,
                        borderBottom: '1px solid rgba(17,24,39,0.08)',
                      }}
                    >
                      <Typography sx={{ color: '#667085' }}>{item.label}</Typography>
                      <Typography sx={{ fontWeight: 900, textAlign: 'right' }}>
                        {item.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                <Button
                  component='a'
                  href={buildWhatsAppUrl(
                    `Halo Junius, saya mau minta simulasi cicilan dan promo BYD M6 ${variant.name}.`,
                  )}
                  target='_blank'
                  rel='noopener noreferrer'
                  variant='contained'
                  sx={{
                    mt: 2.5,
                    borderRadius: '8px',
                    width: '100%',
                    py: 1.15,
                    fontWeight: 900,
                    textTransform: 'none',
                    backgroundColor: '#11805a',
                    '&:hover': { backgroundColor: '#0d6a4b' },
                  }}
                >
                  Minta Simulasi {variant.name}
                </Button>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <Box
        component='section'
        aria-labelledby='alasan-byd-m6-title'
        sx={{
          px: { xs: 2, md: 6 },
          py: { xs: 5, md: 8 },
          backgroundColor: '#f5f7fa',
        }}
      >
        <Box sx={{ maxWidth: 1180, mx: 'auto' }}>
          <Typography
            id='alasan-byd-m6-title'
            component='h2'
            sx={{
              fontWeight: 900,
              color: '#111827',
              lineHeight: 1.12,
              fontSize: { xs: '1.9rem', md: '2.6rem' },
            }}
          >
            Kenapa BYD M6 Cocok untuk Keluarga?
          </Typography>
          <Box
            sx={{
              mt: 3,
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' },
              gap: 2,
            }}
          >
            {m6Highlights.map((item) => (
              <Box
                key={item.title}
                sx={{
                  minHeight: 190,
                  p: 2.5,
                  borderRadius: '8px',
                  backgroundColor: '#fff',
                  border: '1px solid rgba(17,24,39,0.08)',
                }}
              >
                <Typography component='h3' sx={{ fontWeight: 900, fontSize: '1.1rem' }}>
                  {item.title}
                </Typography>
                <Typography sx={{ mt: 1.2, color: '#4b5563', lineHeight: 1.65 }}>
                  {item.text}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <Box
        component='section'
        aria-labelledby='test-drive-byd-m6-title'
        sx={{
          px: { xs: 2, md: 6 },
          py: { xs: 5, md: 8 },
          backgroundColor: '#fff',
        }}
      >
        <Box
          sx={{
            maxWidth: 1180,
            mx: 'auto',
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '0.95fr 1.05fr' },
            gap: { xs: 3, md: 5 },
            alignItems: 'center',
          }}
        >
          <Box
            component='img'
            src={m63}
            alt='Test drive BYD M6 MPV listrik keluarga di BYD BSD Serpong'
            loading='lazy'
            sx={{
              width: '100%',
              minHeight: { xs: 260, md: 420 },
              objectFit: 'cover',
              borderRadius: '8px',
            }}
          />
          <Box>
            <Typography
              id='test-drive-byd-m6-title'
              component='h2'
              sx={{
                fontWeight: 900,
                color: '#111827',
                lineHeight: 1.12,
                fontSize: { xs: '1.9rem', md: '2.6rem' },
              }}
            >
              Test Drive BYD M6 di BYD BSD Serpong
            </Typography>
            <Typography sx={{ mt: 1.5, color: '#4b5563', lineHeight: 1.75 }}>
              Booking test drive M6 bersama Junius untuk merasakan langsung kabin
              6-7 seater, fitur V2L, kenyamanan baris kedua, dan karakter berkendara
              MPV listrik keluarga. Area konsultasi meliputi:
            </Typography>
            <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {serviceAreas.map((area) => (
                <Box
                  key={area}
                  sx={{
                    px: 1.4,
                    py: 0.7,
                    borderRadius: '8px',
                    backgroundColor: '#edf4ff',
                    color: '#1f5fc8',
                    fontWeight: 900,
                    fontSize: '0.88rem',
                  }}
                >
                  {area}
                </Box>
              ))}
            </Box>
            <Box sx={{ mt: 3, display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              <Button
                component='a'
                href={buildWhatsAppUrl(
                  'Halo Junius, saya mau booking test drive BYD M6 di BYD BSD Serpong.',
                )}
                target='_blank'
                rel='noopener noreferrer'
                variant='contained'
                sx={{
                  borderRadius: '8px',
                  px: 3,
                  py: 1.15,
                  textTransform: 'none',
                  fontWeight: 900,
                  backgroundColor: '#2d6cdf',
                  '&:hover': { backgroundColor: '#1f5fc8' },
                }}
              >
                Booking Test Drive
              </Button>
              <Button
                component={RouterLink}
                to='/blog/byd-m6-mpv-listrik-keluarga'
                variant='outlined'
                sx={{
                  borderRadius: '8px',
                  px: 3,
                  py: 1.15,
                  textTransform: 'none',
                  fontWeight: 900,
                  borderColor: '#2d6cdf',
                  color: '#2d6cdf',
                  '&:hover': {
                    borderColor: '#1f5fc8',
                    backgroundColor: '#edf4ff',
                  },
                }}
              >
                Baca Review BYD M6
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box
        component='section'
        aria-labelledby='faq-byd-m6-title'
        sx={{
          px: { xs: 2, md: 6 },
          py: { xs: 5, md: 8 },
          backgroundColor: '#f5f7fa',
        }}
      >
        <Box sx={{ maxWidth: 960, mx: 'auto' }}>
          <Typography
            id='faq-byd-m6-title'
            component='h2'
            sx={{
              fontWeight: 900,
              color: '#111827',
              lineHeight: 1.12,
              fontSize: { xs: '1.9rem', md: '2.6rem' },
              mb: 2.5,
            }}
          >
            FAQ BYD M6
          </Typography>
          <Box sx={{ display: 'grid', gap: 1.5 }}>
            {m6Faqs.map((faq) => (
              <Box
                key={faq.question}
                component='details'
                sx={{
                  p: 2.2,
                  borderRadius: '8px',
                  backgroundColor: '#fff',
                  border: '1px solid rgba(17,24,39,0.08)',
                  '& summary': {
                    cursor: 'pointer',
                    fontWeight: 900,
                    color: '#111827',
                  },
                }}
              >
                <Box component='summary'>{faq.question}</Box>
                <Typography sx={{ mt: 1.2, color: '#4b5563', lineHeight: 1.7 }}>
                  {faq.answer}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <Box
        component='section'
        sx={{
          px: { xs: 2, md: 6 },
          py: { xs: 5, md: 8 },
          backgroundColor: '#111827',
          color: '#fff',
          textAlign: 'center',
        }}
      >
        <Box sx={{ maxWidth: 880, mx: 'auto' }}>
          <Typography
            component='h2'
            sx={{
              fontWeight: 900,
              lineHeight: 1.12,
              fontSize: { xs: '1.9rem', md: '2.7rem' },
            }}
          >
            Siap Cek Promo, Cicilan, dan Jadwal Test Drive BYD M6?
          </Typography>
          <Typography
            sx={{
              mt: 1.5,
              color: 'rgba(255,255,255,0.78)',
              lineHeight: 1.75,
            }}
          >
            Hubungi Junius untuk info harga BYD M6 terbaru, pilihan varian 6-7
            seater, warna, promo, dan jadwal test drive di BYD BSD Serpong.
          </Typography>
          <Button
            component='a'
            href={buildWhatsAppUrl(
              'Halo Junius, saya mau cek harga terbaru, promo, varian, warna, dan jadwal test drive BYD M6.',
            )}
            target='_blank'
            rel='noopener noreferrer'
            variant='contained'
            sx={{
              mt: 3,
              borderRadius: '8px',
              px: 3.5,
              py: 1.2,
              textTransform: 'none',
              fontWeight: 900,
              backgroundColor: '#11805a',
              '&:hover': { backgroundColor: '#0d6a4b' },
            }}
          >
            Hubungi Junius via WhatsApp
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

export default JelajahiM6;
